// src/components/notes/CalloutNoteUI.jsx
//
// useCalloutNotes hook'unun UI parçaları — JbzViewer ve editör-önizleme ortak
// kullanır. Stiller mevcut viewer CSS'lerinden (tek kaynak, görünüm birebir).
import React from 'react';
import API_URL from '../../config';
import './CalloutNoteUI.css';

// Not-ekle butonu + paydaş filtresi. `.dxf-viewer-controls` içine gömülür.
export function CalloutNoteButtons({ noteMode, toggleNoteMode, notes, hiddenAuthors, setHiddenAuthors, filterOpen, setFilterOpen }) {
  return (
    <>
      <button
        className={noteMode ? 'active' : ''}
        onClick={(e) => { e.stopPropagation(); toggleNoteMode(); }}
        title={noteMode ? 'Not modundan cik' : 'Not ekle'}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
      </button>
      {notes.length > 0 && (() => {
        const authorMap = new Map();
        for (const n of notes) {
          const key = `${n.authorName || 'Anonim'}|${n.authorRole || ''}`;
          if (!authorMap.has(key)) authorMap.set(key, { key, name: n.authorName || 'Anonim', role: n.authorRole || null, count: 0 });
          authorMap.get(key).count++;
        }
        const authors = [...authorMap.values()].sort((a, b) => a.name.localeCompare(b.name, 'tr'));
        const anyHidden = authors.some(a => hiddenAuthors.has(a.key));
        return (
          <div className="jbz-filter-wrap" style={{ position: 'relative', display: 'inline-block' }}>
            <button
              className={anyHidden ? 'active' : ''}
              onClick={(e) => { e.stopPropagation(); setFilterOpen(o => !o); }}
              title={anyHidden ? `${authors.filter(a => hiddenAuthors.has(a.key)).length} paydaş gizli` : 'Paydaş filtresi'}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z"/></svg>
            </button>
            {filterOpen && (
              <div className="jbz-filter-dropdown" onClick={e => e.stopPropagation()}>
                <div className="jbz-filter-header">
                  <span>Paydaş Notları</span>
                  <div className="jbz-filter-actions">
                    <button onClick={() => setHiddenAuthors(new Set())}>Hepsi</button>
                    <button onClick={() => setHiddenAuthors(new Set(authors.map(a => a.key)))}>Hiçbiri</button>
                  </div>
                </div>
                <div className="jbz-filter-list">
                  {authors.map(a => {
                    const hidden = hiddenAuthors.has(a.key);
                    return (
                      <label key={a.key} className="jbz-filter-item">
                        <input
                          type="checkbox"
                          checked={!hidden}
                          onChange={() => setHiddenAuthors(prev => {
                            const next = new Set(prev);
                            if (next.has(a.key)) next.delete(a.key); else next.add(a.key);
                            return next;
                          })}
                        />
                        <span className="jbz-filter-name">{a.name}</span>
                        {a.role && <span className="jbz-filter-role">{a.role}</span>}
                        <span className="jbz-filter-count">{a.count}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })()}
    </>
  );
}

// Textarea overlay + ipuçları. three-container içine (position:relative) gömülür.
export function CalloutNoteOverlay({ noteStep, textareaPos, editingId, notes, textareaRef, handleNoteSubmit, handleNoteDelete, handleNoteTextKeyDown, noteMode, CALLOUT_CSS }) {
  return (
    <>
      {noteStep === 2 && textareaPos && (() => {
        const editing = editingId ? notes.find(n => n.id === editingId) : null;
        const fmtDate = (iso) => {
          try { return new Date(iso).toLocaleString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
          catch { return ''; }
        };
        const initials = editing?.authorName
          ? editing.authorName.split(/\s+/).map(s => s[0]).filter(Boolean).slice(0, 2).join('').toUpperCase()
          : '?';
        return (
          <div className="jbz-note-input" style={{ left: textareaPos.x, top: textareaPos.y }}>
            {editing && editing.authorName && (
              <div className="jbz-note-header">
                <div className="jbz-note-author">
                  {editing.authorAvatar
                    ? <img className="jbz-note-avatar" src={API_URL + editing.authorAvatar} alt="" />
                    : <span className="jbz-note-avatar jbz-note-avatar-initials">{initials}</span>}
                  <div className="jbz-note-author-info">
                    <span className="jbz-note-author-name">{editing.authorName}</span>
                    {editing.authorRole && <span className="jbz-note-author-role">{editing.authorRole}</span>}
                  </div>
                </div>
                {editing.createdAt && (
                  <span className="jbz-note-date" title={editing.updatedAt && editing.updatedAt !== editing.createdAt ? `Güncellendi: ${fmtDate(editing.updatedAt)}` : ''}>
                    {fmtDate(editing.createdAt)}
                  </span>
                )}
              </div>
            )}
            <textarea
              ref={textareaRef}
              rows={3}
              placeholder="Not yaz... (Enter kaydet, Esc iptal)"
              onKeyDown={handleNoteTextKeyDown}
              onBlur={handleNoteSubmit}
            />
            {editingId && (
              <button className="jbz-note-delete" onClick={(e) => { e.stopPropagation(); handleNoteDelete(editingId); }}>
                Sil
              </button>
            )}
          </div>
        );
      })()}
      {noteMode && noteStep === 0 && <div className="dxf-viewer-hint" style={{ left: 10, right: 'auto', color: CALLOUT_CSS }}>Hedef noktaya tikla</div>}
      {noteMode && noteStep === 1 && <div className="dxf-viewer-hint" style={{ left: 10, right: 'auto', color: CALLOUT_CSS }}>Kutu konumuna tikla</div>}
    </>
  );
}
